/**
 * Achievements overlay — lists all achievements grouped by category, with
 * per-entry progress bars, tier dots, and hidden-until-unlocked entries for
 * `hidden: true` defs.
 *
 * Follows the Trader/Forge overlay pattern: full-screen backdrop, centred
 * beveled panel, close button, sparse per-frame `update`.
 */

import type { FederatedPointerEvent } from 'pixi.js';
import { Container, Graphics, Text } from 'pixi.js';
import {
  draw3DButton, drawBevelPanel, drawInsetRect,
  buildCloseButton, buildPanelTitleBand, type OverlayResult,
} from './PanelUtils';
import { ACCENT, ACCENT_DIM, ACCENT_DARK } from './LayoutConstants';
import { createScrollBar } from './ScrollBar';
import { attachScrollDrag, type ScrollDragHandle } from './ScrollablePanel';
import { ACHIEVEMENTS, type AchievementDef, type AchievementCategory } from '../achievements/AchievementData';
import type { GameStateManager } from '../states/GameStateManager';

const PANEL_W = 840;
const PANEL_H = 620;
const PAD = 16;
const HEADER_H = 40;
const TAB_H = 32;
const ROW_H = 56;
const SCROLLBAR_W = 10;

const CATEGORIES: AchievementCategory[] = [
  'Combat', 'Progression', 'Exploration', 'Loot', 'Challenge', 'Meta', 'Misc',
];

export interface AchievementsOverlayResult extends OverlayResult {
  update: (dt: number) => void;
}

export function buildAchievementsOverlay(
  manager: GameStateManager,
  screenW: number,
  screenH: number,
  onClose: () => void,
): AchievementsOverlayResult {
  const root = new Container();

  // Backdrop — eats clicks / wheel so nothing behind reacts.
  const backdrop = new Graphics();
  backdrop.rect(0, 0, screenW, screenH);
  backdrop.fill({ color: 0x000000, alpha: 0.6 });
  backdrop.eventMode = 'static';
  backdrop.cursor = 'default';
  backdrop.on('pointerdown', (e: FederatedPointerEvent) => e.stopPropagation());
  root.addChild(backdrop);

  const panelW = Math.min(PANEL_W, screenW - 40);
  const panelH = Math.min(PANEL_H, screenH - 40);
  const panel = new Container();
  const panelX = Math.round((screenW - panelW) / 2);
  const panelY = Math.round((screenH - panelH) / 2);
  panel.position.set(panelX, panelY);
  root.addChild(panel);

  const bg = new Graphics();
  drawBevelPanel(bg, 0, 0, panelW, panelH, {
    fill: 0x0c0e14, border: 0x1a2030, radius: 8, depth: 4,
    rivets: true, accentEdge: ACCENT,
  });
  panel.addChild(bg);

  buildPanelTitleBand(panel, panelW, 'ACHIEVEMENTS', ACCENT, ACCENT_DARK);

  // Summary (unlocked tiers / total tiers).
  const ach = manager.achievements;
  const totalAvail = ach.totalAvailableTiers();
  const totalUnlocked = ach.totalUnlockedTiers();
  const pct = totalAvail > 0 ? Math.floor((totalUnlocked / totalAvail) * 100) : 0;
  const summary = new Text({
    text: `${totalUnlocked} / ${totalAvail}  (${pct}%)`,
    style: { fontFamily: 'Offside', fontSize: 13, fill: ACCENT_DIM, fontWeight: '600' },
  });
  summary.anchor.set(1, 0.5);
  summary.position.set(panelW - 60, HEADER_H / 2);
  panel.addChild(summary);

  buildCloseButton(panel, panelW - 38, 9, ACCENT, ACCENT_DARK, onClose);

  // Tabs.
  let activeCategory: AchievementCategory = 'Combat';
  const tabsY = HEADER_H + 4;
  const tabsContainer = new Container();
  tabsContainer.position.set(PAD, tabsY);
  panel.addChild(tabsContainer);
  const tabWidth = Math.floor((panelW - PAD * 2) / CATEGORIES.length);

  function rebuildTabs() {
    tabsContainer.removeChildren().forEach((c) => c.destroy({ children: true }));
    for (let i = 0; i < CATEGORIES.length; i++) {
      const cat = CATEGORIES[i]!;
      const isActive = cat === activeCategory;
      const tab = new Container();
      tab.position.set(i * tabWidth, 0);
      const g = new Graphics();
      draw3DButton(g, tabWidth - 2, TAB_H, isActive ? ACCENT : ACCENT_DARK);
      tab.addChild(g);
      const label = new Text({
        text: cat.toUpperCase(),
        style: {
          fontFamily: 'Offside', fontSize: 11,
          fill: isActive ? 0x10141e : 0xaabbcc, fontWeight: '700',
        },
      });
      label.anchor.set(0.5);
      label.position.set((tabWidth - 2) / 2, TAB_H / 2);
      tab.addChild(label);
      tab.eventMode = 'static';
      tab.cursor = 'pointer';
      tab.on('pointerdown', (e: FederatedPointerEvent) => {
        e.stopPropagation();
        if (activeCategory !== cat) {
          activeCategory = cat;
          rebuildTabs();
          rebuildList();
        }
      });
      tabsContainer.addChild(tab);
    }
  }

  // List area.
  const listY = tabsY + TAB_H + 8;
  const listH = panelH - listY - PAD;
  const listW = panelW - PAD * 2;

  const listFrame = new Graphics();
  drawInsetRect(listFrame, PAD, listY, listW, listH, 4, 0x060810, 0x1a2030);
  panel.addChild(listFrame);

  const listContent = new Container();
  listContent.position.set(PAD, listY);
  listContent.eventMode = 'static';
  panel.addChild(listContent);

  const listMask = new Graphics();
  listMask.rect(0, 0, listW, listH);
  listMask.fill(0xffffff);
  listMask.position.set(PAD, listY);
  panel.addChild(listMask);
  listContent.mask = listMask;

  // Transparent hit-area to capture wheel events across the list (gaps between rows).
  const listHit = new Graphics();
  listHit.rect(0, 0, listW, listH);
  listHit.fill({ color: 0x000000, alpha: 0 });
  listHit.eventMode = 'static';
  listContent.addChildAt(listHit, 0);

  let scrollY = 0;
  let contentH = 0;
  let scrollBar: ReturnType<typeof createScrollBar> | null = null;

  function setScroll(y: number) {
    const maxY = Math.max(0, contentH - listH);
    scrollY = Math.max(0, Math.min(maxY, y));
    listContent.position.y = listY - scrollY;
    const frac = maxY > 0 ? scrollY / maxY : 0;
    scrollBar?.setFraction(frac);
  }

  const scrollDrag: ScrollDragHandle = attachScrollDrag({
    hitArea: listHit,
    onDelta: (dy: number) => {
      if (contentH <= listH) return;
      setScroll(scrollY + dy);
    },
  });

  function snapshotForGetValue() {
    // AchievementSystem holds context in a private field; re-expose via a
    // controlled accessor here by reading `ctx` reflectively, mirroring the
    // internal `statsWithContext()` path used by `recheck()`.
    const ctx = (ach as unknown as { ctx?: Record<string, number> }).ctx ?? {};
    return Object.assign({}, ach.stats, ctx) as Parameters<AchievementDef['getValue']>[0];
  }

  function buildRow(def: AchievementDef, y: number, width: number): Container {
    const row = new Container();
    row.position.set(0, y);

    const tiersUnlocked = ach.unlocked[def.id] ?? 0;
    const isFullyLocked = tiersUnlocked === 0;
    const isHidden = def.hidden === true && isFullyLocked;
    const fullyDone = tiersUnlocked >= def.tiers.length;

    const rowBg = new Graphics();
    const fillColor = fullyDone ? 0x1a2418 : isFullyLocked ? 0x10131a : 0x121520;
    drawBevelPanel(rowBg, 0, 0, width, ROW_H - 4, {
      fill: fillColor,
      border: 0x1a2030,
      radius: 4,
      depth: 2,
      accentEdge: fullyDone ? 0x66cc66 : isFullyLocked ? 0x333944 : ACCENT_DIM,
    });
    row.addChild(rowBg);

    const displayName = isHidden ? '???' : def.name;
    const nameText = new Text({
      text: displayName,
      style: {
        fontFamily: 'Offside', fontSize: 14,
        fill: isFullyLocked ? 0x8899aa : ACCENT,
        fontWeight: '700',
      },
    });
    nameText.position.set(10, 6);
    row.addChild(nameText);

    const snapshot = snapshotForGetValue();
    const curValue = def.getValue(snapshot);

    let descText: string;
    if (isHidden) {
      descText = 'Hidden — unlock the first tier to reveal.';
    } else {
      const nextIdx = Math.min(tiersUnlocked, def.tiers.length - 1);
      const threshold = def.tiers[nextIdx]!;
      const formatted = def.formatValue ? def.formatValue(threshold) : String(threshold);
      descText = def.description.replace(/\{X\}/g, formatted);
    }
    const desc = new Text({
      text: descText,
      style: {
        fontFamily: 'Offside', fontSize: 11,
        fill: isFullyLocked ? 0x667788 : 0xaabbcc,
      },
    });
    desc.position.set(10, 26);
    row.addChild(desc);

    // Tier dots.
    const dotR = 5;
    const dotGap = 14;
    const dotsW = def.tiers.length * dotGap;
    const dotsX = width - dotsW - 10;
    const dotsY = 10;
    for (let i = 0; i < def.tiers.length; i++) {
      const dot = new Graphics();
      dot.circle(dotsX + i * dotGap + dotR, dotsY + dotR, dotR);
      dot.fill(i < tiersUnlocked ? 0xffcc44 : 0x333944);
      dot.stroke({ color: 0x1a2030, width: 1 });
      row.addChild(dot);
    }

    // Progress bar (skip for hidden / fully-unlocked).
    if (!isHidden && !fullyDone) {
      const nextIdx = Math.min(tiersUnlocked, def.tiers.length - 1);
      const threshold = def.tiers[nextIdx]!;
      const frac = def.minTracker
        ? (curValue > 0 ? Math.min(1, threshold / curValue) : 0)
        : Math.min(1, curValue / Math.max(1, threshold));
      const barW = 140;
      const barH = 6;
      const barX = width - barW - 10;
      const barY = ROW_H - 16;
      const bar = new Graphics();
      bar.rect(barX, barY, barW, barH);
      bar.fill(0x1a2030);
      bar.rect(barX, barY, barW * frac, barH);
      bar.fill(ACCENT);
      row.addChild(bar);

      const displayCur = def.formatValue ? def.formatValue(curValue) : String(Math.floor(curValue));
      const displayTarget = def.formatValue ? def.formatValue(threshold) : String(threshold);
      const progText = new Text({
        text: `${displayCur} / ${displayTarget}`,
        style: { fontFamily: 'Offside', fontSize: 10, fill: 0x8899aa },
      });
      progText.anchor.set(1, 0);
      progText.position.set(width - 10, barY - 11);
      row.addChild(progText);
    }

    return row;
  }

  function rebuildList() {
    // Clear prior rows but keep the hit-area at index 0.
    while (listContent.children.length > 1) {
      const c = listContent.children[1]!;
      listContent.removeChild(c);
      c.destroy({ children: true });
    }
    const innerW = listW - SCROLLBAR_W - 4;
    const defs = ACHIEVEMENTS.filter((d) => d.category === activeCategory);
    let y = 4;
    for (const def of defs) {
      listContent.addChild(buildRow(def, y, innerW));
      y += ROW_H;
    }
    contentH = y;

    // Resize hit-area to match content so wheel works everywhere.
    listHit.clear();
    listHit.rect(0, 0, innerW, Math.max(listH, contentH));
    listHit.fill({ color: 0x000000, alpha: 0 });

    if (scrollBar) {
      panel.removeChild(scrollBar.container);
      scrollBar.destroy();
      scrollBar = null;
    }
    if (contentH > listH) {
      const maxY = contentH - listH;
      scrollBar = createScrollBar({
        trackHeight: listH,
        contentHeight: contentH,
        visibleHeight: listH,
        onChange: (frac) => {
          scrollY = frac * maxY;
          listContent.position.y = listY - scrollY;
        },
      });
      scrollBar.container.position.set(panelW - PAD - SCROLLBAR_W, listY);
      panel.addChild(scrollBar.container);
    }
    setScroll(0);
  }

  rebuildTabs();
  rebuildList();

  return {
    container: root,
    update: (_dt: number) => { /* static panel; no per-frame animation */ },
    dispose: () => scrollDrag.destroy(),
  };
}
